import { Component, OnInit, OnDestroy, HostListener, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { switchMap, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  WorkPackageView,
  GridColumn,
  ViewDefinitionPayload,
  ViewColumnsPayload,
  ViewFiltersPayload,
  ViewFilterRule,
  ViewSharePayload,
  mapAccessLevelToVisibility,
  ViewVisibility,
} from '../../../models/work-package-view.model';
import {
  FilterOperatorDef,
  isBetweenOperator,
  isMultiValueOperator,
} from '../../../models/filter.model';
import { FilterOperator, ColumnType, AccessLevel } from '../../../services/views/views.enums';
import { RecordState, StatusID } from '../../../enums/user';
import { FilterItem } from '../saved-views.component';
import { ReportingModule, toReportingModule } from '../../../services/reporting-views/reporting-views.service';
import { REPORTING_VIEWS_SERVICE_TOKEN } from '../../../services/reporting-views/reporting-views.token';
import { ViewsFilterService } from '../../../services/views/views-filter.service';
import { ToastService } from '../../../services/toast/toast.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { CentralizedTagsService } from '../../../services/centralized-tags/centralized-tags.service';

export interface CreateViewDialogData {
  view?: WorkPackageView; // For edit mode
  companyId: number;
  projectId: number;
  userId: number;
  userName: string;
  moduleType?: string; // 'work-package', 'inspection', etc.
  availableColumns?: GridColumn[]; // Columns that can be toggled
  userOptions?: { id: number | string; name: string; partnerCompanyId?: number }[]; // User list for dropdown filters
  milestoneOptions?: { id: number; name: string }[]; // Milestone list for milestone-type filters
  /** When true, only show the column selector (no name/access/filter sections) */
  columnsOnlyMode?: boolean;
  /** When true, treat as create even if a view is passed (used for clone) */
  cloneMode?: boolean;
  /** When true, the dialog was opened from a dashboard widget — show the "saved as dashboard settings only" notice. */
  dashboardContext?: boolean;
  /** In dashboard context: the bound tblDashboardView id to UPDATE. Undefined → create a new dashboard view. */
  dashboardViewId?: number;
  /**
   * In dashboard context: the name the widget's dashboard view is stored under. Dashboard views are
   * saved by name, so each widget keeps its own — the form's name (the source view's) would merge
   * every widget showing that view into one row.
   */
  dashboardViewName?: string;
}

interface ColumnGroup {
  type: ColumnType;
  label: string;
  columns: GridColumn[];
}

@Component({
  selector: 'app-create-view-dialog',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-36/COL-37), out of scope for this hotfix
  standalone: false,
  templateUrl: './create-view-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-view-dialog.component.scss',
})
export class CreateViewDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<CreateViewDialogComponent>>(MatDialogRef);
  data = inject<CreateViewDialogData>(MAT_DIALOG_DATA);
  private reportingViewsService = inject(REPORTING_VIEWS_SERVICE_TOKEN);
  private viewsFilterService = inject(ViewsFilterService);
  private toastService = inject(ToastService);
  private utilsService = inject(UtilsService);
  private centralizedTagsService = inject(CentralizedTagsService);

  viewForm: FormGroup;
  isEditMode = false;
  columnsOnlyMode = false;
  isDashboardContext = false;
  ownerName: string;
  /** Columns a view can show — filter-only columns (`isSelectable: false`) left out. */
  availableColumns: GridColumn[] = [];
  /** Every column the filter builder can use, filter-only ones included. */
  private filterColumns: GridColumn[] = [];
  selectedColumns = new Set<string>();
  isSaving = false;
  isAdmin = false;

  // Filter builder properties
  appliedFilters: FilterItem[] = [];
  showFilterMenu = false;
  filterLogic: 'AND' | 'OR' = 'AND';
  filterSearchTerm = '';
  private _columnSearchTerm = '';
  private _groupedColumnsCache: ColumnGroup[] | null = null;

  get columnSearchTerm(): string { return this._columnSearchTerm; }
  set columnSearchTerm(value: string) {
    if (this._columnSearchTerm !== value) {
      this._columnSearchTerm = value;
      this._groupedColumnsCache = null; // invalidate on search change
    }
  }
  userOptions: { id: number | string; name: string; partnerCompanyId?: number }[] = [];
  milestoneOptions: { id: number; name: string }[] = [];
  sharedMilestoneId = 0;
  stateOptions: string[] = [];
  activeUserFilterIndex: number | null = null;
  userFilterSearchTerm = '';
  private filterValueDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly requiredColumnFields: Set<string>;

  // Tags filter support
  tagOptions: { id: string; name: string }[] = [];
  tagsLoaded = false;
  tagsLoading = false;

  // Dropdown smart positioning
  filterMenuPosition = 'bottom-left';
  userFilterDropdownPosition = 'bottom-left';

  constructor() {
    const data = this.data;
    const utilsService = this.utilsService;

    this.isEditMode = !!data.view && !data.cloneMode;
    this.columnsOnlyMode = !!data.columnsOnlyMode;
    this.isDashboardContext = !!data.dashboardContext;
    this.ownerName = data.view?.ownerName || data.userName;
    this.filterColumns = data.availableColumns || [];
    this.availableColumns = this.filterColumns.filter(col => col.isSelectable !== false);
    this.userOptions = data.userOptions || [];
    this.milestoneOptions = data.milestoneOptions || [];
    this.isAdmin = utilsService.isAdmin();
    // The one column a view of this module can never hide — its record's name.
    const REQUIRED_COLUMN: Record<string, string> = {
      'work-order': 'woCode',
      scheduler: 'eventName',
      action: 'caption',
      inspection: 'title',
      booking: 'personName',
    };
    this.requiredColumnFields = new Set<string>([REQUIRED_COLUMN[data.moduleType ?? ''] ?? 'name']);
  }

  ngOnInit(): void {
    this.stateOptions = this.utilsService.getStateList();
    this.initializeForm();
    this.initializeFromView();
  }

  /**
   * Initialize selected columns and filters from existing view
   */
  private initializeFromView(): void {
    if (!this.data.view) {
      // New view — select all columns by default
      this.selectedColumns = new Set(this.availableColumns.map(col => col.field));
      this.ensureRequiredColumnsSelected();
      return;
    }

    // Edit mode — initialize from view using service
    const view = this.data.view;

    // Initialize selected columns from view.columns
    this.selectedColumns = this.viewsFilterService.initializeSelectedColumns(
      view.columns,
      this.availableColumns,
    );
    this.ensureRequiredColumnsSelected();

    // Initialize filter logic from existing view
    if (view.filters?.logic) {
      this.filterLogic = view.filters.logic as 'AND' | 'OR';
    }

    // For work-order module, strip default rules so they don't appear in the builder UI
    const WO_DEFAULT_FIELDS = ['parentworkorderid', 'recordstate'];
    const rulesForDisplay = this.data.moduleType === 'work-order'
      ? (view.filters?.rules || []).filter(
          r => !WO_DEFAULT_FIELDS.includes(r.field?.toLowerCase() || '')
        )
      : (view.filters?.rules || []);

    // Restore shared milestone ID from either the current or legacy saved rule formats.
    this.sharedMilestoneId = this.resolveSharedMilestoneId(rulesForDisplay);

    // Initialize applied filters from view.filters.rules using service (milestoneName rule skipped)
    this.appliedFilters = this.viewsFilterService.filtersFromRules(
      rulesForDisplay,
      this.filterColumns,
    );

    // Lazily load tag options if any filter targets the tags field
    if (rulesForDisplay.some(r => r.field?.toLowerCase() === 'tags')) {
      this.loadTagOptions();
    }
  }

  private resolveSharedMilestoneId(rules: ViewFilterRule[]): number {
    const savedId = this.viewsFilterService.extractSharedMilestoneId(rules);
    if (savedId) {
      return savedId;
    }

    const milestoneRule = rules.find(
      (rule: ViewFilterRule & { milestoneId?: number | string }) => rule.type === ColumnType.MILESTONE
    );

    if (
      milestoneRule?.field === 'milestoneName' &&
      milestoneRule.value &&
      this.milestoneOptions.length
    ) {
      const match = this.milestoneOptions.find(
        milestone => milestone.name === milestoneRule.value
      );

      if (match) {
        return match.id;
      }
    }

    if (milestoneRule?.milestoneId != null) {
      return Number(milestoneRule.milestoneId);
    }

    return 0;
  }

  /**
   * Initialize the form with data
   */
  initializeForm(): void {
    if (this.columnsOnlyMode) {
      // Minimal form — no name/access validation needed
      this.viewForm = this.fb.group({ name: [''], accessLevel: ['private'] });
      return;
    }
    this.viewForm = this.fb.group({
      name: [
        this.data.view?.name || '',
        [Validators.required, Validators.maxLength(100)],
      ],
      accessLevel: [
      {
        value: this.isAdmin ? (this.data.view?.accessLevel || ViewVisibility.Private) : ViewVisibility.Private,
        disabled: !this.isAdmin,
      },
      Validators.required,
    ],
    });
  }

  get isTeamSelected(): boolean {
    return this.viewForm?.get('accessLevel')?.value === AccessLevel.TEAM;
  }

  /**
   * Toggle column selection
   */
  toggleColumn(columnField: string): void {
    if (this.isRequiredColumn(columnField)) {
      this.selectedColumns.add(columnField);
      return;
    }

    if (this.selectedColumns.has(columnField)) {
      this.selectedColumns.delete(columnField);
    } else {
      this.selectedColumns.add(columnField);
    }
  }

  /**
   * Check if column is selected
   */
  isColumnSelected(columnField: string): boolean {
    return this.selectedColumns.has(columnField);
  }

  /**
   * Select or deselect all columns
   */
  toggleAllColumns(selectAll: boolean): void {
    if (selectAll) {
      this.selectedColumns = new Set(this.availableColumns.map(col => col.field));
    } else {
      this.selectedColumns = new Set(
        this.availableColumns
          .filter(col => this.isRequiredColumn(col.field))
          .map(col => col.field)
      );
    }
  }

  /**
   * Check if all columns are selected
   */
  areAllColumnsSelected(): boolean {
    return this.selectedColumns.size === this.availableColumns.length;
  }

  isRequiredColumn(columnOrField: GridColumn | string): boolean {
    const field = typeof columnOrField === 'string' ? columnOrField : columnOrField.field;
    return this.requiredColumnFields.has(field);
  }

  getGroupedColumns(): ColumnGroup[] {
    if (this._groupedColumnsCache) return this._groupedColumnsCache;

    const filteredColumns = this.getFilteredColumnsForSelection();
    const groups: ColumnGroup[] = [
      { type: ColumnType.STANDARD, label: 'Standard Columns', columns: [] },
      { type: ColumnType.CUSTOM, label: 'Custom Columns', columns: [] },
      { type: ColumnType.MILESTONE, label: 'Milestone Columns', columns: [] },
    ];

    for (const column of filteredColumns) {
      const group = groups.find(item => item.type === (column.columnType || ColumnType.STANDARD));
      if (group) {
        group.columns.push(column);
      }
    }

    this._groupedColumnsCache = groups.filter(group => group.columns.length > 0);
    return this._groupedColumnsCache;
  }

  areAllGroupColumnsSelected(group: ColumnGroup): boolean {
    return group.columns.every(column => this.selectedColumns.has(column.field));
  }

  toggleColumnGroup(group: ColumnGroup, selectAll: boolean): void {
    if (selectAll) {
      group.columns.forEach(column => this.selectedColumns.add(column.field));
      return;
    }

    group.columns.forEach(column => {
      if (!this.isRequiredColumn(column)) {
        this.selectedColumns.delete(column.field);
      }
    });
  }

  getFilteredColumnsForSelection(): GridColumn[] {
    if (!this.columnSearchTerm.trim()) {
      return this.availableColumns;
    }

    const searchLower = this.columnSearchTerm.trim().toLowerCase();
    return this.availableColumns.filter(column => {
      const typeLabel = this.getColumnTypeLabel(column.columnType).toLowerCase();
      return (
        column.headerText.toLowerCase().includes(searchLower) ||
        column.field.toLowerCase().includes(searchLower) ||
        typeLabel.includes(searchLower)
      );
    });
  }

  getColumnTypeLabel(type?: string): string {
    switch (type) {
      case ColumnType.CUSTOM:
        return 'Custom';
      case ColumnType.MILESTONE:
        return 'Milestone';
      default:
        return 'Standard';
    }
  }

  getGroupToggleLabel(group: ColumnGroup): string {
    const typeLabel = group.label.toLowerCase();
    return this.areAllGroupColumnsSelected(group)
      ? `Deselect all ${typeLabel}`
      : `Select all ${typeLabel}`;
  }

  /**
   * Toggle filter logic between AND and OR
   */
  toggleFilterLogic(): void {
    this.filterLogic = this.filterLogic === 'AND' ? 'OR' : 'AND';
  }

  /**
   * Set filter logic to a specific value
   */
  setFilterLogic(logic: 'AND' | 'OR'): void {
    this.filterLogic = logic;
  }

  /**
   * Toggle filter menu dropdown
   */
  toggleFilterMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.showFilterMenu = !this.showFilterMenu;
    if (this.showFilterMenu && event) {
      this.filterMenuPosition = this.calculateDropdownPosition(event.currentTarget as HTMLElement, 400, 200);
    }
    if (!this.showFilterMenu) {
      this.filterSearchTerm = '';
    }
  }

  /**
   * Close filter menu
   */
  closeFilterMenu(): void {
    this.showFilterMenu = false;
    this.filterSearchTerm = '';
  }

  /**
   * Calculate smart dropdown position based on available viewport space.
   */
  private calculateDropdownPosition(
    trigger: HTMLElement,
    dropdownHeight: number,
    dropdownWidth: number
  ): string {
    if (!trigger) return 'bottom-left';

    const { top, bottom, left } = trigger.getBoundingClientRect();

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const safeWidth = dropdownWidth > 320 ? dropdownWidth : 320;
    const safeHeight = dropdownHeight > 320 ? dropdownHeight : 320;

    const spaceBelow = viewportHeight - bottom;
    const spaceAbove = top;
    const spaceRight = viewportWidth - left;

    const openAbove = spaceBelow < safeHeight && spaceAbove > spaceBelow;
    const alignRight = spaceRight < safeWidth || left > viewportWidth * 0.6;

    if (openAbove) {
      return alignRight ? 'top-right' : 'top-left';
    }

    return alignRight ? 'bottom-right' : 'bottom-left';
  }
  /**
   * Get columns available for filtering (excludes already applied filters and non-meaningful ID fields)
   */
  getAvailableColumnsForFilter(): GridColumn[] {
    return this.viewsFilterService.getAvailableColumnsForFilter(
      this.filterColumns,
      this.appliedFilters,
    );
  }

  /**
   * Get filtered columns based on search term
   */
  getFilteredColumnsForFilter(): GridColumn[] {
    const availableColumns = this.getAvailableColumnsForFilter();
    if (!this.filterSearchTerm.trim()) {
      return availableColumns;
    }
    const searchLower = this.filterSearchTerm.toLowerCase();
    return availableColumns.filter(col =>
      col.headerText.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Get dynamic operators for a column based on its dataType.
   * For user fields, use dropdown operators instead.
   */
  getOperatorsForColumn(column: GridColumn): FilterOperatorDef[] {
    return this.viewsFilterService.getOperatorsForColumn(
      column,
      this.viewsFilterService.isUserField(column),
    );
  }

  /**
   * Check if the current operator is a 'between' type
   */
  isBetween(operator: string): boolean {
    return this.viewsFilterService.isBetween(operator);
  }

  /**
   * Check if the current operator is a multi-value type (in/notin)
   */
  isMulti(operator: string): boolean {
    return this.viewsFilterService.isMulti(operator);
  }

  isNoValue(operator: string): boolean {
    return this.viewsFilterService.isNoValue(operator);
  }

  /**
   * Get input type based on column dataType
   */
  getInputType(column: GridColumn): string {
    return this.viewsFilterService.getInputType(column);
  }

  /**
   * Check if a column is a numeric type
   */
  isNumberField(column: GridColumn): boolean {
    return this.viewsFilterService.isNumberField(column);
  }

  /**
   * Get context-aware placeholder for a filter value input
   */
  getPlaceholder(column: GridColumn, boundary?: 'start' | 'end'): string {
    return this.viewsFilterService.getPlaceholder(column, boundary);
  }

  /**
   * Check if a column is a user field (createdBy, modifiedBy, etc.)
   */
  isUserField(column: GridColumn): boolean {
    return this.viewsFilterService.isUserField(column);
  }

  /**
   * Check if a column is a dropdown type
   */
  isDropdownColumn(column: GridColumn): boolean {
    return this.viewsFilterService.isDropdownColumn(column, this.userOptions);
  }

  /**
   * Check if a column is the state field (should render as a state dropdown)
   */
  isStateField(column: GridColumn): boolean {
    return column.field?.toLowerCase() === 'state';
  }

  getFilteredLookupOptions(column: GridColumn): { id: string | number; name: string }[] {
    const options = column.lookupOptions || [];
    if (!this.userFilterSearchTerm.trim()) return options;
    const term = this.userFilterSearchTerm.trim().toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(term));
  }

  getFilteredDropdownOptions(column: GridColumn, isState: boolean): string[] {
    const options = isState ? this.stateOptions : (column.options || []);
    if (!this.userFilterSearchTerm.trim()) return options;
    const term = this.userFilterSearchTerm.trim().toLowerCase();
    return options.filter((o: string) => o.toLowerCase().includes(term));
  }

  /**
   * Check if a column uses ID-based lookup options (e.g., status, type)
   */
  isLookupField(column: GridColumn): boolean {
    return !!column.lookupOptions && column.lookupOptions.length > 0;
  }

  /**
   * Resolve a lookup ID to its display name
   */
  getLookupLabel(column: GridColumn, id: string | number): string {
    if (!column.lookupOptions) return String(id);
    const option = column.lookupOptions.find((o) => String(o.id) === String(id));
    return option?.name || String(id);
  }

  /**
   * Get user name by ID from loaded userOptions
   */
  getUserNameById(id: string | number): string {
    const numId = Number(id);
    const user = this.userOptions.find(u => Number(u.id) === numId);
    return user?.name || String(id);
  }

  isTagsField(column: GridColumn): boolean {
    return column.field?.toLowerCase() === 'tags';
  }

  isWorkPackageModule(): boolean {
    return this.data.moduleType === 'work-package';
  }

  isMilestoneColumn(column: GridColumn): boolean {
    return this.isWorkPackageModule() && column.columnType === ColumnType.MILESTONE;
  }

  isMilestoneNameColumn(column: GridColumn): boolean {
    return column.field?.toLowerCase() === 'milestonename';
  }

  shouldShowMilestoneSelector(column: GridColumn): boolean {
    return this.isMilestoneColumn(column) && !this.isMilestoneNameColumn(column);
  }

  getMilestoneName(milestoneId: number): string {
    return this.milestoneOptions.find(m => m.id === milestoneId)?.name ?? String(milestoneId);
  }

  isFirstMilestoneFilter(index: number): boolean {
    return this.appliedFilters.findIndex(f => this.shouldShowMilestoneSelector(f.column)) === index;
  }

  updateSharedMilestoneId(id: number): void {
    if (!this.isWorkPackageModule()) return;
    this.sharedMilestoneId = id;
  }

  loadTagOptions(): void {
    if (this.tagsLoaded || this.tagsLoading) return;
    const projectId = this.data.projectId;
    const companyId = String(this.data.companyId);
    if (!projectId || !companyId) return;
    this.tagsLoading = true;
    this.centralizedTagsService.getConfig(projectId, companyId).subscribe({
      next: (config) => {
        this.tagOptions = (config.tags || [])
          .filter(t => t.status === StatusID.Active)
          .map(t => ({ id: t.id, name: t.name }));
        this.tagsLoaded = true;
        this.tagsLoading = false;
      },
      error: () => { this.tagsLoading = false; },
    });
  }

  getFilteredTagOptions(): { id: string; name: string }[] {
    if (!this.userFilterSearchTerm.trim()) return this.tagOptions;
    const term = this.userFilterSearchTerm.trim().toLowerCase();
    return this.tagOptions.filter(t => t.name.toLowerCase().includes(term));
  }

  getTagLabel(id: string): string {
    return this.tagOptions.find(t => t.id === id)?.name || id;
  }

  /**
   * Add a new filter with the default operator for the column's data type
   */
  addFilter(column: GridColumn): void {
    if (this.isTagsField(column)) {
      this.loadTagOptions();
    }
    const defaultOp = this.viewsFilterService.getDefaultOperatorForColumn(
      column,
      this.viewsFilterService.isUserField(column),
    );
    this.appliedFilters.push({
      column,
      operator: defaultOp,
      value: '',
      valueTo: '',
      multiValues: [],
    });
    if (this.shouldShowMilestoneSelector(column) && !this.sharedMilestoneId && this.milestoneOptions.length > 0) {
      this.sharedMilestoneId = this.milestoneOptions[0].id;
    }
    this.showFilterMenu = false;
  }

  /**
   * Remove a filter by index
   */
  removeFilter(index: number): void {
    this.appliedFilters.splice(index, 1);
  }

  /**
   * Update filter operator - resets values when operator type changes
   */
  updateFilterOperator(index: number, operator: string): void {
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].operator = operator;
      this.appliedFilters[index].value = '';
      this.appliedFilters[index].valueTo = '';
      this.appliedFilters[index].multiValues = [];
    }
  }

  /**
   * Update filter value with debounce (single value input)
   */
  updateFilterValue(index: number, value: string): void {
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].value = value;
    }
  }

  /**
   * Update the 'valueTo' field for 'between' operator
   */
  updateFilterValueTo(index: number, value: string): void {
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].valueTo = value;
    }
  }

  /**
   * Add a value to multi-value filter (in/notin). Triggered on Enter key.
   */
  addMultiValue(index: number, value: string, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const trimmed = value?.trim();
    if (!trimmed || !this.appliedFilters[index]) {
      return;
    }
    if (!this.appliedFilters[index].multiValues) {
      this.appliedFilters[index].multiValues = [];
    }
    if (!this.appliedFilters[index].multiValues!.includes(trimmed)) {
      this.appliedFilters[index].multiValues!.push(trimmed);
    }
    if (event?.target) {
      (event.target as HTMLInputElement).value = '';
    }
  }

  /**
   * Remove a value from multi-value filter
   */
  removeMultiValue(filterIndex: number, valueIndex: number): void {
    if (this.appliedFilters[filterIndex]?.multiValues) {
      this.appliedFilters[filterIndex].multiValues!.splice(valueIndex, 1);
    }
  }

  /**
   * Handle dropdown multi-select change for user/dropdown columns
   */
  updateMultiSelectValues(index: number, values: string[]): void {
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].multiValues = values;
    }
  }

  /**
   * Extract selected values from a multi-select element
   */
  getSelectedOptions(event: Event): string[] {
    const selectElement = event.target as HTMLSelectElement;
    return Array.from(selectElement.selectedOptions).map(option => option.value);
  }

  /**
   * Check if any filters are applied
   */
  hasAppliedFilters(): boolean {
    return this.appliedFilters.length > 0;
  }

  /**
   * Clear all applied filters
   */
  clearAllFilters(): void {
    this.appliedFilters = [];
    this.filterLogic = 'AND';
  }

  /**
   * Build columnFilters object from applied filters for saving to view config
   */
  private buildColumnFilters(): Record<string, { operator: string; value: string | number | (string | number)[]; dataType: string }[]> {
    const columnFilters: Record<string, { operator: string; value: string | number | (string | number)[]; dataType: string }[]> = {};
    for (const filter of this.appliedFilters) {
      const operator = filter.operator;
      let value: string | number | (string | number)[];
      if (isBetweenOperator(operator)) {
        if (!filter.value && !filter.valueTo) continue;
        value = [filter.value || '', filter.valueTo || ''];
      } else if (isMultiValueOperator(operator)) {
        if (!filter.multiValues?.length) continue;
        value = [...filter.multiValues];
      } else {
        if (!filter.value) continue;
        value = filter.value;
      }
      columnFilters[filter.column.field] = [{ operator, value, dataType: filter.column.dataType || 'string' }];
    }
    return columnFilters;
  }

  ngOnDestroy(): void {
    if (this.filterValueDebounceTimer) {
      clearTimeout(this.filterValueDebounceTimer);
    }
  }

  /**
   * Build API-compatible filters payload from applied filters.
   * For work-order module, always prepends default filters (parentWorkOrderId, recordState).
   */
  private buildFiltersPayload(): ViewFiltersPayload {
    const filtersPayload = this.viewsFilterService.buildFiltersPayload(
      this.appliedFilters,
      this.filterLogic,
      this.sharedMilestoneId || undefined,
    );

    if (this.data.moduleType === 'work-order') {
      const defaultRules: ViewFilterRule[] = [
        { type: ColumnType.STANDARD, field: 'parentWorkOrderId', op: FilterOperator.EQUALS, value: '0' },
        { type: ColumnType.STANDARD, field: 'recordState', op: FilterOperator.EQUALS, value: RecordState.Active },
      ];
      filtersPayload.rules = [...defaultRules, ...filtersPayload.rules];
    }

    return filtersPayload;
  }

  /**
   * Build API-compatible columns payload from selected columns
   */
  private buildColumnsPayload(): ViewColumnsPayload {
    this.ensureRequiredColumnsSelected();
    return this.viewsFilterService.buildColumnsPayload(
      this.selectedColumns,
      this.availableColumns,
    );
  }

  private ensureRequiredColumnsSelected(): void {
    this.availableColumns
      .filter(column => this.isRequiredColumn(column))
      .forEach(column => this.selectedColumns.add(column.field));
  }

  /**
   * Save the view via API
   */
  onSave(): void {
    if (this.viewForm.invalid || this.isSaving) {
      return;
    }

    const formValue = this.viewForm.getRawValue();
    const existingView = this.data.view;

    // In columnsOnlyMode, preserve all existing view properties and only update columns
    const payload: ViewDefinitionPayload = this.columnsOnlyMode && existingView
      ? {
          id: existingView.id,
          companyId: this.data.companyId,
          projectId: this.data.projectId,
          name: existingView.name,
          description: existingView.description ?? '',
          visibility: (existingView.visibility ?? ViewVisibility.Private) as ViewVisibility,
          columns: this.buildColumnsPayload(),
          filters: existingView.filters ?? { logic: 'AND', rules: [] },
          createdBy: this.data.userId,
        }
      : {
          // Dashboard context: update the widget's bound dashboard view (or create one when unbound)
          // — never the module view id, which doesn't exist in tblDashboardView.
          // cloneMode: never send an id (force create), otherwise use existing id for updates.
          id: this.isDashboardContext
            ? (this.data.dashboardViewId ?? undefined)
            : (this.data.cloneMode ? undefined : (existingView?.id || undefined)),
          companyId: this.data.companyId,
          projectId: this.data.projectId,
          name: (this.isDashboardContext && this.data.dashboardViewName) || formValue.name.trim(),
          description: '',
          visibility: mapAccessLevelToVisibility(formValue.accessLevel),
          columns: this.buildColumnsPayload(),
          filters: this.buildFiltersPayload(),
          createdBy: this.data.userId,
        };

    this.isSaving = true;

    const apiModule: ReportingModule = toReportingModule(this.data.moduleType ?? '');
    const isTeam = formValue.accessLevel === AccessLevel.TEAM;

    this.reportingViewsService.saveView(payload, apiModule).pipe(
      switchMap((response) => {
        if (!response?.success || !isTeam || !response.id) {
          return of(response);
        }

        // Auto-share with all internal users (partnerCompanyId === 0), excluding the owner
        const internalUserIds = this.userOptions
          .filter(u => (u.partnerCompanyId ?? 0) === 0 && Number(u.id) !== this.data.userId)
          .map(u => Number(u.id))
          .filter(id => id > 0);

        if (internalUserIds.length === 0) {
          return of(response);
        }

        const sharePayload: ViewSharePayload = {
          companyId: this.data.companyId,
          userId: this.data.userId,
          userIds: internalUserIds,
          roleIds: [],
        };

        return this.reportingViewsService.updateShareList(response.id, sharePayload, apiModule).pipe(
          map(() => response)
        );
      })
    ).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response?.success) {
          this.dialogRef.close({
            view: null, // The parent will refresh from API
            action: this.isEditMode ? 'updated' : 'created',
            viewId: response.id,
          });
        } else {
          console.error('Failed to save view:', response?.message);
          if (!response?.networkError) {
            this.handleSaveError(response?.message || 'Failed to save view');
          }
        }
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Error saving view:', err);
        if (err?.status === 0) {
          return;
        }
        const errorMessage = err?.error?.message || err?.message || 'Failed to save view';
        this.handleSaveError(errorMessage);
      },
    });
  }

  /**
   * Handle save errors and show appropriate toast notification
   */
  private handleSaveError(errorMessage: string | undefined): void {
    // Provide fallback if message is empty or "Unknown error"
    const displayMessage = errorMessage && errorMessage.trim() && errorMessage !== 'Unknown error'
      ? errorMessage
      : 'Failed to save view. Please try again.';

    // Check if it's a duplicate key constraint error
    if (displayMessage.toLowerCase().includes('duplicate') &&
        (displayMessage.includes('unique') || displayMessage.includes('UX_'))) {
      this.toastService.showError("Can't create duplicate; it already exists.", 'View Already Exists');
    } else {
      this.toastService.showError(displayMessage, 'Error');
    }
  }

  /**
   * Close the dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Close open dropdowns when clicking outside.
   * Trigger buttons and dropdown panels call stopPropagation, so any click
   * reaching document means "clicked outside".
   */
  @HostListener('document:click')
  onClickOutside(): void {
    if (this.showFilterMenu) {
      this.closeFilterMenu();
    }
    if (this.activeUserFilterIndex !== null) {
      this.closeUserFilterDropdown();
    }
  }

  // ========== USER DROPDOWN METHODS ==========

  toggleUserFilterDropdown(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.activeUserFilterIndex === index) {
      this.closeUserFilterDropdown();
    } else {
      this.activeUserFilterIndex = index;
      this.userFilterSearchTerm = '';
      if (event) {
        this.userFilterDropdownPosition = this.calculateDropdownPosition(event.currentTarget as HTMLElement, 300, 220);
      }
    }
  }

  closeUserFilterDropdown(): void {
    this.activeUserFilterIndex = null;
    this.userFilterSearchTerm = '';
  }

  getFilteredUsers(): { id: number | string; name: string }[] {
    if (!this.userFilterSearchTerm || !this.userFilterSearchTerm.trim()) {
      return this.userOptions;
    }
    const term = this.userFilterSearchTerm.toLowerCase().trim();
    return this.userOptions.filter(u => u.name.toLowerCase().includes(term));
  }

  isUserSelected(filter: FilterItem, userId: number | string): boolean {
    if (this.isMulti(filter.operator)) {
      // Ensure we compare compatible types if needed, though typically API returns consistent types.
      // Safe check: filter.multiValues might be numbers or strings.
      return (filter.multiValues || []).some((val) => val == userId);
    } else {
      return filter.value == userId;
    }
  }

  toggleUserMultiSelection(index: number, userId: number | string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.appliedFilters[index]) return;

    if (this.isMulti(this.appliedFilters[index].operator)) {
      // Multi-select logic
      const filter = this.appliedFilters[index];
      if (!filter.multiValues) {
        filter.multiValues = [];
      }
      
      const valueIndex = filter.multiValues.findIndex((val) => val == userId);
      if (valueIndex > -1) {
        filter.multiValues.splice(valueIndex, 1);
      } else {
        filter.multiValues.push(String(userId));
      }
      
      // Update local reference array new instance to trigger change detection if needed
      filter.multiValues = [...filter.multiValues];
    } else {
      // Single select logic
      this.appliedFilters[index].value = userId;
      this.closeUserFilterDropdown();
    }
  }
}
